import { defineStore } from "pinia";
import { type Component } from "vue";
import { login } from "@/components/GlobleModal/LoginModal/service";
import { useUserStore } from "@/store/user";
import { sensors, PRD_ID, ANONYMOUS_ID, getSafePlatformCode, getSource } from "@/assets/sensors";
import { getAscribeParams } from "@/hooks/useAscribe";
import { message } from "ant-design-vue";
import { accountReg, emailReg } from "@/utils/reg";
import { useCreationTask } from "@/hooks/useCreationTask";
import LoginModeWechat from "@/assets/images/loginModal/login-mode-weChat.svg";
import LoginModeVerificationCode from "@/assets/images/loginModal/login-mode-verificationCode.svg";
import LoginModePassword from "@/assets/images/loginModal/login-mode-password.svg";
import { checkIsH5Environment, getURLSearchParams } from "@/utils";
import { userRightStore } from "@/hooks/useUserRights";
import router from "@/router";
import type { CaptchaTicket } from "@/features/tencent-captcha";
import { buildMobileLoginUrl } from "@/router/guards/mobileLoginUrl";

// 登录方式
export const loginModeOptions = [
  {
    label: "微信登录",
    value: 3,
    icon: LoginModeWechat
  },
  {
    label: "手机验证码",
    value: 1,
    icon: LoginModeVerificationCode
  },
  {
    label: "账号密码",
    value: 2,
    icon: LoginModePassword
  }
];

export const useLoginStore = defineStore("login", {
  state: () => ({
    open: false,
    inviteCode: undefined as string | undefined, // 邀请码
    activeLoginMode: loginModeOptions[0].value, // 当前选中的登录方式
    wechatOpenId: "",
    wechatUnionId: "",
    showComponent: undefined as Component | undefined, // 组件对象，需要用 markRaw 包装赋值
    loginPopUpParams: {
      login_position: "", // 登录时间触发来源
      login_type: "", // 登录方式
      source: "default-default", // 来源渠道
      anonymous_id: "" // 匿名id
    },
    byPhoneCodeLoginPhoneNum: "", // 手机号验证码登录的手机号码
    byPasswordLoginAccount: "", // 账号密码登录方式输入的账号
    resetPasswordState: {
      byLoginModel: true, // 在登录组件最外层点击重置密码
      account: "" // 点击重置密码时，当前输入的账号
    },
    codeByEmailLogin: "", // 首次使用邮箱登录获取的验证码
    /** 是否是测试用户免密登录 */
    isTestLogin: false,
    /** 当前登录会话是否启用滑块（enabled=1 时为 true） */
    phoneCodeCaptchaEnabled: false,
    /** 手机验证码登录滑块票据（首次发码使用） */
    phoneCodeCaptchaTicket: null as CaptchaTicket | null
  }),
  actions: {
    updateWeChatInfo({ openId, unionId }: { openId: string; unionId: string }) {
      this.wechatOpenId = openId;
      this.wechatUnionId = unionId;
    },
    async toLogin(values: any, { updateLoginType = true } = {}) {
      const userStore = useUserStore();
      try {
        const { clearAllTasks } = useCreationTask();

        if (emailReg.test(values.account)) {
          values.loginType = 4;
          values.email = values.account;
          delete values.account;

          if (values.password && updateLoginType) {
            values.loginType = 2;
          }
        }

        const source = getSource();
        const platformCode = getSafePlatformCode();
        const shouldAscribe = sessionStorage.getItem("shouldAscribe");
        const ascribeParams = shouldAscribe ? getAscribeParams() : {};

        const { data: loginData } = await login({
          inviteCode: this.inviteCode,
          wechatOpenId: this.wechatOpenId,
          wechatUnionId: this.wechatUnionId,
          ...values,
          source,
          anonymousId: ANONYMOUS_ID,
          recommendUniqueId: userStore.inviteId,
          recommendModelId: userStore.recommendModelId,
          url: window.location.href,
          platformCode,
          ...ascribeParams
        });
        userStore.updateUserInfo(loginData);
        const userRightsHook = userRightStore();
        await userRightsHook.refreshUserRights();

        // 用户加入团队后，登录后需要弹出toast提示告知他当前进入的是什么版本
        if (userStore.userInfo.teamId) {
          message.success(`当前使用版本为【${userStore.userInfo.curTeam ? "团队版" : "个人版"}】`);
        }

        sensors.login(PRD_ID + "-" + userStore.userInfo.uniqueId);

        clearAllTasks();
        this.open = false;

        const redirect = getURLSearchParams("redirect");
        if (redirect) {
          router.replace({
            path: redirect as string
          });
        }
      } catch (error: any) {}
    },
    resetLoginState() {
      this.open = false;
      this.showComponent = undefined;
      this.activeLoginMode = 3;
      this.byPhoneCodeLoginPhoneNum = "";
      this.byPasswordLoginAccount = "";
      this.wechatOpenId = "";
      this.wechatUnionId = "";
      this.isTestLogin = false;
      this.phoneCodeCaptchaEnabled = false;
      this.phoneCodeCaptchaTicket = null;
    },
    resetPassword({ byLoginModel, account }: { byLoginModel: boolean; account: string }) {
      this.resetPasswordState.byLoginModel = byLoginModel;

      // 判断用户当前输入的账号是否合规
      if (accountReg.test(account)) {
        this.resetPasswordState.account = account;
      } else {
        this.resetPasswordState.account = "";
      }
    },
    async handleDeviceEnv() {
      // 检测当前环境
      const isH5Environment = await checkIsH5Environment();

      if (isH5Environment) {
        window.location.href = buildMobileLoginUrl(window.location.href, router.currentRoute.value.fullPath);
      } else {
        this.open = true;
      }
    }
  }
});

export default useLoginStore;
